import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocalizationContext } from '@amzn/react-arb-tools'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { getLocalizationContext, applyDocumentLocale } from '@amzn/jwo-store-management-platform-components'

/**
 * Keeps the arb-tools localization context in step with the locale in redux.
 *
 * Without this, only two paths ever applied a locale: the Provision page and the
 * language switcher. Sessions that land straight on a route — which is what the
 * native iOS/Android shells do, opening `...&locale=xx-XX#/home` — never entered
 * provisioning, so the `?locale=` param was parsed into redux and then ignored,
 * leaving the UI in the default locale.
 *
 * Mount this once at the app root so every route is covered. It also re-applies
 * when provisioning later resolves a store-country default, keeping the document
 * language and the rendered strings from disagreeing.
 */
export const useLocalizationSync = (): void => {
    const locale = useSelector(getSelectedLocale)
    const { setLocalizationContext } = useLocalizationContext()
    // Skip the redundant first write: the bundle is already initialized from
    // <html lang> (set at bootstrap from this same resolved locale), so applying
    // it again on mount would discard loaded bundles for no visible change.
    const appliedLocale = useRef<string | undefined>(locale)

    useEffect(() => {
        if (!locale || locale === appliedLocale.current) {
            return
        }

        appliedLocale.current = locale
        setLocalizationContext(getLocalizationContext(locale))
        applyDocumentLocale(locale)
    }, [locale, setLocalizationContext])
}
